import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Books, Plus, PencilSimple, Trash, Eye } from "@phosphor-icons/react";
import { InlineRichHtmlEditor, htmlToPlain, sanitizeHtml } from "@/components/RichHtmlEditorDialog";

const AUDIENCE_LABEL = { sales: "Sales", support: "Support" };

export default function StaffKnowledgeBases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [contentOverLimit, setContentOverLimit] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/staff-knowledge-bases");
      setRows(data || []);
      return data || [];
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Deep-link edit from reading page (?edit=id)
  useEffect(() => {
    if (!isAdmin) return;
    const editId = searchParams.get("edit");
    if (!editId || loading) return;
    const row = rows.find((r) => r.id === editId);
    if (row) {
      setContentOverLimit(false);
      setEditor({
        id: row.id,
        name: row.name,
        content: row.content || "",
        audience: row.audience || "sales",
      });
      setSearchParams({}, { replace: true });
    }
  }, [isAdmin, searchParams, rows, loading, setSearchParams]);

  const save = async () => {
    if (!isAdmin) return;
    const name = (editor?.name || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    if (!editor?.audience) { toast.error("Select Sales or Support"); return; }
    if (contentOverLimit) {
      toast.error("Content is over the character limit");
      return;
    }
    const content = sanitizeHtml(editor?.content || "");
    const body = { name, content, audience: editor.audience, status: "active" };
    try {
      if (editor.id) {
        await api.patch(`/staff-knowledge-bases/${editor.id}`, body);
        toast.success("Knowledge base updated");
      } else {
        await api.post("/staff-knowledge-bases", body);
        toast.success("Knowledge base created");
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (row) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete “${row.name}”?`)) return;
    try {
      await api.delete(`/staff-knowledge-bases/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const previewText = (html) => {
    const plain = htmlToPlain(html || "");
    if (!plain) return "—";
    return plain.length > 120 ? `${plain.slice(0, 120)}…` : plain;
  };

  return (
    <div data-testid="staff-knowledge-bases-page">
      <PageHeader
        title="Knowledge Bases"
        subtitle={
          isAdmin
            ? "Internal docs for Sales and Support. Sales staff only see Sales bases; Support staff only see Support bases."
            : "Knowledge bases for your group."
        }
      >
        {isAdmin && (
          <Button
            className="rounded-lg"
            onClick={() => {
              setContentOverLimit(false);
              setEditor({ name: "", content: "", audience: "sales" });
            }}
            data-testid="skb-new-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" weight="bold" /> New knowledge base
          </Button>
        )}
      </PageHeader>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Books}
          title="No knowledge bases yet"
          description={
            isAdmin
              ? "Create a Sales or Support knowledge base for staff to reference."
              : "No knowledge bases are available for your group yet."
          }
          testid="skb-empty"
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="skb-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Preview</TableHead>
                <TableHead className="w-36">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`skb-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-medium">
                      {AUDIENCE_LABEL[r.audience] || r.audience}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                    {previewText(r.content)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2"
                        onClick={() => navigate(`/staff-knowledge-bases/${r.id}`)}
                        aria-label="View"
                        title="View article"
                        data-testid={`skb-view-${r.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2"
                            onClick={() => {
                              setContentOverLimit(false);
                              setEditor({
                                id: r.id,
                                name: r.name,
                                content: r.content || "",
                                audience: r.audience || "sales",
                              });
                            }}
                            aria-label="Edit"
                            title="Edit"
                            data-testid={`skb-edit-${r.id}`}
                          >
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-destructive"
                            onClick={() => remove(r)}
                            aria-label="Delete"
                            title="Delete"
                            data-testid={`skb-delete-${r.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-2xl w-[min(94vw,42rem)] max-h-[90vh] flex flex-col" data-testid="skb-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editor?.id ? "Edit knowledge base" : "New knowledge base"}</DialogTitle>
            <DialogDescription>
              Assign to Sales or Support. Sales users cannot see Support knowledge bases.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-0.5">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input
                  data-testid="skb-name"
                  value={editor?.name || ""}
                  onChange={(e) => setEditor((s) => ({ ...s, name: e.target.value }))}
                  placeholder="Sales playbook"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Group</Label>
                <Select
                  value={editor?.audience || "sales"}
                  onValueChange={(v) => setEditor((s) => ({ ...s, audience: v }))}
                >
                  <SelectTrigger data-testid="skb-audience"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Content</Label>
              {editor && (
                <InlineRichHtmlEditor
                  key={editor.id || "new-skb"}
                  value={editor.content || ""}
                  maxLength={100000}
                  placeholder="Scripts, FAQs, internal notes…"
                  testid="skb-content"
                  minHeightClass="min-h-[280px]"
                  maxHeightClass="max-h-[45vh]"
                  onChange={(html) => {
                    const plain = htmlToPlain(html);
                    setContentOverLimit(plain.length > 100000);
                    setEditor((s) => ({ ...s, content: html }));
                  }}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setEditor(null)}>Cancel</Button>
            <Button className="rounded-lg" onClick={save} disabled={contentOverLimit} data-testid="skb-save-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
